import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Field, Input, Message, Toast } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { bindClick, bindFormChange, getAssetTypeIcon, getUserAssetTypeName, getInventoryItemAssetTypeName } from '../SiteUtil';
import { Asset, AssetAddOptions, AssetType, SetLoading, UserAssetType, InventoryItemAssetType } from '../SiteClasses';
import { useUser } from '../hooks';
import UploadBox, { UploadBoxRef } from './UploadBox';
import Validator from '../Validator';
import API from '../API';
import moment from 'moment';

interface AddAssetModalProps {
	open: boolean,
	onClose: () => void,
	dateId?: number,
	expenseId?: number,
	workerId?: number,
	userId?: number,
	inventoryItemId?: number,
	onAdded?: (asset: Asset & {
		worker_asset_id?: number,
		user_asset_id?: number,
	}, options?: AssetAddOptions)=>void,
}

interface AssetForm{
	date_id?: number,
	expense_id?: number,
	type_id: number,
	asset_type: UserAssetType,
	asset_name: string,
	description: string,
	reference: string,
	file: File,
}

var AddAssetModal = (props: AddAssetModalProps) => {
	var { user } = useUser();
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [form, setForm] = useState<AssetForm>(null);
	var uploadBoxRef = useRef<UploadBoxRef>(null);

	useEffect(()=>{
		setModalPrompts(null);
		setForm({
			type_id: null,
			asset_type: null,
			asset_name: '',
			description: '',
			reference: '',
			file: null,
		});
		if(uploadBoxRef.current){
			uploadBoxRef.current.clear();
		}
	}, [props.open]);

	var onFileSelected = (file: File)=>{
		var type : AssetType = null;
		if(!file){
			setForm({
				...form,
				type_id: null,
				file: null,
			})
			return;
		}
		if(file.type=='application/pdf'){
			type = AssetType.PDF;
		}else if(file.type.startsWith('image/')){
			type = AssetType.IMAGE;
		}else if(file.type.startsWith('video/')){
			type = AssetType.VIDEO;
			var maxSizeMB = 100;
			if(file.size / (1024 * 1024) > maxSizeMB){
				return Toast.error(`El video excede el tamaño máximo de ${maxSizeMB}MB`);
			}
		}
		if(!type) return Toast.error('El archivo no es un formato válido');
		setForm({
			...form,
			type_id: type,
			file,
		});
	}

	var handleSubmit = async (setLoading: SetLoading) => {
		var { valid, prompts } = Validator(form, {
			asset_name: [{
				rule: 'length', params: [3, 128], label: 'Nombre de archivo'
			}],
			type_id: [{
				rule: 'empty', label: 'Tipo de archivo'
			}],
			asset_type: !(props.workerId || props.userId || props.inventoryItemId) ? [] : [{
				rule: 'empty', prompt: 'Favor de seleccionar el tipo de documento',
			}],
			reference: !props.expenseId ? [] : [
				{ rule: 'empty', label: 'Folio' },
				{ rule: 'maxLength', params: [36], label: 'Folio' },
			],
			file: [{
				rule: 'empty', prompt: 'Favor de seleccionar el archivo a subir'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);

		try{
			var res = await API.createAsset(form.type_id, form.asset_name, form.description, form.file, {
				date_id: props.dateId,
				worker_id: props.workerId,
				user_id: props.userId,
				expense_id: props.expenseId,
				reference: props.expenseId ? form.reference : undefined,
				inventory_item_id: props.inventoryItemId,
				inventory_item_asset_type: !!(props.inventoryItemId) ? form.asset_type as unknown as InventoryItemAssetType : null,
				user_asset_type: !!(props.workerId || props.userId) ? form.asset_type : null,
			});
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha agreado el archivo');

			var newAsset: Asset = {
				asset_id: res.data.asset_id,
				type_id: form.type_id,
				asset_name: form.asset_name,
				description: form.description,
				date_created: moment().unix(),
				user_creator: user.user_id,
				user_creator_name: user.name,
				url: res.data.url,
				download_url: res.data.download_url,
			};
			props.onAdded?.(newAsset, {
				user_asset_type: form.asset_type,
				user_asst_type_name: getUserAssetTypeName(form.asset_type),
				...( !!(props.inventoryItemId) ? {
					inventory_item_asset_type: form.asset_type,
					inventory_item_asset_type_name: getInventoryItemAssetTypeName(form.asset_type as unknown as InventoryItemAssetType),
				} : {} ),

			});
			props.onClose();
		}catch(err){
			setModalPrompts(['Hubo un error inesperado agregando el archivo (LCL-1)']);
		}finally{
			setLoading(false);
		}
	}

	var onFormChange = bindFormChange(form, setForm);
	return <Modal open={props.open} onClose={props.onClose} size="tiny">
		<Modal.Header>Agregar archivo</Modal.Header>
		{!!form && (
			<Modal.Content>
				<Input label='Nombre del archivo' value={form.asset_name} onChange={onFormChange('asset_name')} />
				{!!(props.expenseId) && (
					<Input
						label='Folio'
						value={form.reference}
						onChange={onFormChange('reference')}
					/>
				)}
				{!!(props.workerId || props.userId) && (
					<Field label="Tipo de documento">
						<Dropdown
							selection
							value={form.asset_type}
							onChange={onFormChange('asset_type', true)}
							placeholder='Selecciona tipo'
							options={[
								{ value: UserAssetType.ID, text: 'Identificación' },
								{ value: UserAssetType.ADDRESS, text: 'Comprobante de domicilio' },
								{ value: UserAssetType.CIF, text: 'Carta de situación fiscal' },
								{ value: UserAssetType.CURP, text: 'CURP' },
							]}
						/>
					</Field>
				)}
				{!!(props.inventoryItemId) && (
					<Field label="Tipo de documento">
						<Dropdown
							selection
							value={form.asset_type}
							onChange={onFormChange('asset_type', true)}
							placeholder='Selecciona tipo'
							options={[
								{ value: InventoryItemAssetType.INVOICE, text: 'Factura de compra' },
							]}
						/>
					</Field>
				)}
				<Input textarea
					label="Descripción"
					value={form.description}
					onChange={onFormChange('description')}
				/>
				<Field label='Archivo' comment='Adjunta una imagen, PDF o video'>
					<UploadBox
						ref={uploadBoxRef}
						icon={getAssetTypeIcon(form.type_id)}
						type={'normal'}
						text={form.file ? form.file.name : null}
						maxFileSize={8*1024*1024}
						onFile={onFileSelected}
					/>
				</Field>
				<Message list={modalPrompts} type="error" />
			</Modal.Content>
		)}
		<Modal.Actions>
			<Button text="Cancelar" basic onClick={bindClick(props.onClose)} />
			<Button text="Agregar" color="black"  onClick={handleSubmit} />
		</Modal.Actions>
	</Modal>
};

export default AddAssetModal;
